import { Card, Empty } from "antd";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { INK, CATEGORICAL, pctChange, periodLabel, previousLabel } from "../crmDashboardUtils";

const CURRENT = CATEGORICAL[0];

const TrendTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="crm-tip">
      <div className="crm-tip-title">{p.label}</div>
      <div className="crm-tip-row">
        <i style={{ background: CURRENT }} />
        <span>New leads</span>
        <b>{p.count}</b>
      </div>
      <div className="crm-tip-row">
        <i className="dashed" />
        <span>{p.prev_label}</span>
        <b>{p.prev_count}</b>
      </div>
    </div>
  );
};

const LeadsTrend = ({ data = [], period, compact = false }) => {
  const total = data.reduce((s, d) => s + d.count, 0);
  const prevTotal = data.reduce((s, d) => s + d.prev_count, 0);
  const change = pctChange(total, prevTotal);
  const empty = total === 0 && prevTotal === 0;

  return (
    <Card className="crm-card" variant="borderless">
      <div className="crm-card-head">
        <div>
          <div className="crm-card-title">Lead inflow</div>
          <div className="crm-card-sub">
            <b className="crm-card-figure">{total.toLocaleString()}</b> new leads {periodLabel(period)}
            {change !== null && Math.abs(change) >= 0.05 && (
              <span className={`crm-trend ${change >= 0 ? "up" : "down"}`} style={{ marginLeft: 8 }}>
                {change >= 0 ? "+" : ""}
                {change.toFixed(0)}%
              </span>
            )}
          </div>
        </div>
        <div className="crm-legend">
          <span><i style={{ background: CURRENT }} />This period</span>
          <span><i className="dashed" />{previousLabel(period)}</span>
        </div>
      </div>

      {empty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No new leads in this period" style={{ padding: compact ? "16px 0" : "48px 0" }} />
      ) : (
        <div className="crm-chart" role="img" aria-label={`New leads per day, ${total} in total ${periodLabel(period)}`}>
          <ResponsiveContainer width="100%" height={compact ? 120 : 260}>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="crmLeadFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CURRENT} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={CURRENT} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={INK.grid} strokeOpacity={0.7} vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: INK.axis }}
                tick={{ fill: INK.muted, fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={28}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fill: INK.muted, fontSize: 11 }}
                width={40}
              />
              <Tooltip content={<TrendTip />} cursor={{ stroke: INK.axis, strokeDasharray: "3 3" }} />
              <Area
                type="monotone"
                dataKey="prev_count"
                stroke={INK.muted}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="none"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke={CURRENT}
                strokeWidth={2}
                fill="url(#crmLeadFill)"
                dot={false}
                activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2, fill: CURRENT }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="crm-sr-only">
        <table>
        <caption>New leads per day</caption>
        <thead>
          <tr><th>Date</th><th>New leads</th><th>Previous period</th></tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.date}><td>{d.label}</td><td>{d.count}</td><td>{d.prev_count}</td></tr>
          ))}
        </tbody>
      </table>
      </div>
    </Card>
  );
};

export default LeadsTrend;
